import React from 'react';

import { type UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { type MakeGuestInParkingCardFormValues } from 'src/features/cards/components/MakeGuestInParkingCardModalForm';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useGetParkingGuestReasons } from 'src/features/parkingManagement/api/ReasonsForGuest/useGetParkingGuestReasons';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';

type PointActionFormProps = {
    form: UseFormReturn<MakeGuestInParkingCardFormValues>;
    onSubmit: (values: MakeGuestInParkingCardFormValues) => void;
};

const OTHER_REASON = 'reason_other';

function MakeGuestInParkingCardForm({ form, onSubmit }: PointActionFormProps) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const permissionMakeGuestOther = useHasPermissions([PERMISSION.VEHICLE_STAY_MAKE_GUEST_OTHER]);
    const { reason } = form.watch();
    const { data: guestReasonsData } = useGetParkingGuestReasons({
        parkingId: parkingId || '',
        config: { enabled: !!parkingId },
    });

    const guestReasonsMappedData = React.useMemo(() => {
        if (!guestReasonsData) return [];

        const reasonsData = guestReasonsData.results.map((reason) => ({ label: reason.name, value: reason.id }));
        const otherReasonOption = permissionMakeGuestOther
            ? [{ label: formatMessage(cardMessages.other), value: OTHER_REASON }]
            : [];

        return [...reasonsData, ...otherReasonOption];
    }, [guestReasonsData, permissionMakeGuestOther]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(cardMessages.reason)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} defaultValue={field.value ?? ''}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={formatMessage(cardMessages.guestReasonPlaceholder)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {guestReasonsMappedData.map((reason) => (
                                            <SelectItem key={reason.value} value={reason.value}>
                                                {reason.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {reason === OTHER_REASON && (
                <FormField
                    control={form.control}
                    name="reason_other"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(cardMessages.otherReason)}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder={formatMessage(cardMessages.otherReason)}
                                        value={field.value}
                                        onChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
        </Form>
    );
}

export default MakeGuestInParkingCardForm;
